import type { TextFieldProps } from '@mui/material/TextField';

import { Controller, useFormContext } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

// ----------------------------------------------------------------------

export type SearchableOption = {
  value: string;
  label: string;
  [key: string]: any;
};

type BaseProps = {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  options: SearchableOption[];
  /** Multi-select (default: false) */
  multiple?: boolean;
  /** Disable input/control */
  disabled?: boolean;
  /** Width / sizing */
  fullWidth?: boolean;
  sx?: TextFieldProps['sx'];
  /** Server-side search callback. If provided, built-in client filter is disabled. */
  onSearch?: (query: string) => void;
  /** Called when listbox is scrolled near bottom – use to fetch next page. */
  onLoadMore?: () => void;
  /** Whether more pages can be loaded. */
  hasMore?: boolean;
  /** Show loading spinner inside the listbox / input adornment. */
  loading?: boolean;
  /** Debounce in ms for `onSearch` (default 400) */
  debounceMs?: number;
  /** Min characters before triggering onSearch (default 0) */
  minSearchLength?: number;
  /** Optional render for each option (label only by default) */
  renderOptionLabel?: (option: SearchableOption) => React.ReactNode;
};

export type RHFSearchableSelectProps = BaseProps;

// ----------------------------------------------------------------------

const defaultClientFilter = createFilterOptions<SearchableOption>({
  matchFrom: 'any',
  stringify: (option) => option.label,
});

export function RHFSearchableSelect({
  name,
  label,
  placeholder,
  helperText,
  options,
  multiple = false,
  disabled,
  fullWidth = true,
  sx,
  onSearch,
  onLoadMore,
  hasMore = false,
  loading = false,
  debounceMs = 400,
  minSearchLength = 0,
  renderOptionLabel,
}: RHFSearchableSelectProps) {
  const { control, setValue } = useFormContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef<string>('');

  // Debounced server search – fires only when query actually changes
  const triggerSearch = useCallback(
    (raw: string) => {
      if (!onSearch) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const q = raw.trim();
        if (q.length < minSearchLength) return;
        if (q === lastSearchRef.current) return;
        lastSearchRef.current = q;
        onSearch(q);
      }, debounceMs);
    },
    [onSearch, debounceMs, minSearchLength]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  // Build a quick lookup map – memoized so we don't rebuild every render
  const optionsMap = useMemo(() => {
    const m = new Map<string, SearchableOption>();
    options.forEach((o) => m.set(o.value, o));
    return m;
  }, [options]);

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      if (!onLoadMore || !hasMore || loading) return;
      const node = event.currentTarget;
      // Trigger when within 80px of bottom
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 80) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, loading]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Resolve current value(s) from string id(s) -> option object(s)
        const value: any = multiple
          ? Array.isArray(field.value)
            ? field.value
                .map((v: string) => optionsMap.get(v) || { value: v, label: v })
            : []
          : field.value
          ? optionsMap.get(field.value) || { value: field.value, label: field.value }
          : null;

        return (
          <Autocomplete
            multiple={multiple}
            disabled={disabled}
            fullWidth={fullWidth}
            disableCloseOnSelect={multiple}
            options={options}
            value={value}
            onInputChange={(_, val, reason) => {
              if (reason === 'input') triggerSearch(val);
            }}
            onChange={(_, newValue) => {
              if (multiple) {
                setValue(
                  name,
                  (newValue as SearchableOption[]).map((o) => o.value),
                  { shouldValidate: true, shouldDirty: true }
                );
              } else {
                setValue(name, (newValue as SearchableOption | null)?.value ?? '', {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            }}
            getOptionLabel={(opt: any) => opt?.label ?? ''}
            isOptionEqualToValue={(opt: any, val: any) => opt?.value === val?.value}
            // Disable built-in filter when server-search is in use
            filterOptions={onSearch ? (x) => x : defaultClientFilter}
            loading={loading}
            sx={sx}
            ListboxProps={{
              onScroll: handleListboxScroll,
              sx: { maxHeight: 320 },
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.value}>
                {multiple && (
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                )}
                {renderOptionLabel ? renderOptionLabel(option) : option.label}
              </li>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option: any, index: number) => (
                <Chip
                  size="small"
                  variant="soft"
                  label={option.label}
                  {...getTagProps({ index })}
                  key={option.value}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder || 'Search...'}
                error={!!error}
                helperText={error ? error.message : helperText}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
              />
            )}
            noOptionsText={
              loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} /> Loading...
                </Box>
              ) : (
                'No results'
              )
            }
          />
        );
      }}
    />
  );
}

